type OurError =
    | (Error & { columnNumber: number | undefined; lineNumber: number | undefined })
    | ErrorEvent
    | PromiseRejectionEvent;

const trySerialize = (value: unknown): string => {
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const safeStringify = (value: unknown): string => {
    const seen = new WeakSet();
    return JSON.stringify(value, (_, val: unknown) => {
        if (typeof val === 'object' && val !== null) {
            if (seen.has(val)) {
                return '[Circular]';
            }
            seen.add(val);
        }
        return val;
    });
};

const serializeEventProps = (errorEvent: OurError): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const key in errorEvent) {
        try {
            const val = (errorEvent as unknown as Record<string, unknown>)[key];
            if (typeof val !== 'function') {
                result[key] = val;
            }
        } catch {
            // ignore non-readable properties
        }
    }
    return result;
};

export const buildErrorDetails = (
    errorEvent: OurError
): {
    message: string;
    filename: string;
    lineno: string;
    colno: string;
    stack: string;
    eventType: string;
    serializedEvent: Record<string, unknown> | null;
} => {
    const eventType =
        'reason' in errorEvent
            ? 'PromiseRejectionEvent'
            : errorEvent instanceof ErrorEvent
              ? 'ErrorEvent'
              : 'Error';

    // Resource load errors (img, script, link failing) — target is the DOM element
    if (
        'target' in errorEvent &&
        errorEvent.target instanceof HTMLElement &&
        !(errorEvent.target instanceof Window)
    ) {
        const el = errorEvent.target as HTMLElement & { src?: string; href?: string };
        const resourceUrl = el.src ?? el.getAttribute('href') ?? 'unknown';
        return {
            message: `Resource load failed: <${el.tagName.toLowerCase()}> ${resourceUrl}`,
            filename: resourceUrl,
            lineno: 'n.a.',
            colno: 'n.a.',
            stack: 'n.a.',
            eventType,
            serializedEvent: null
        };
    }

    // PromiseRejectionEvent — reason can be Error, string, object, null, undefined
    if ('reason' in errorEvent) {
        const reason = errorEvent.reason as unknown;
        let message: string;
        let stack: string;

        if (reason instanceof Error) {
            message = reason.message || 'Error without message';
            stack = reason.stack ?? 'n.a.';
        } else if (typeof reason === 'string') {
            message = reason || 'Promise rejected with empty string';
            stack = 'n.a.';
        } else if (reason == null) {
            message = 'Promise rejected with no reason (null/undefined)';
            stack = 'n.a.';
        } else {
            message = `Promise rejected with non-Error value: ${trySerialize(reason)}`;
            stack = 'n.a.';
        }

        return {
            message,
            filename: 'n.a.',
            lineno: 'n.a.',
            colno: 'n.a.',
            stack,
            eventType,
            serializedEvent: serializeEventProps(errorEvent)
        };
    }

    // ErrorEvent / Error
    const rawMessage =
        ('message' in errorEvent && errorEvent.message) ||
        ('error' in errorEvent && (errorEvent as ErrorEvent).error?.message) ||
        '';

    // Cross-origin scripts intentionally hide details
    const isCrossOrigin =
        rawMessage === 'Script error.' &&
        'lineno' in errorEvent &&
        (errorEvent as ErrorEvent).lineno === 0;

    const message = isCrossOrigin
        ? 'Script error (cross-origin — details blocked by browser CORS policy)'
        : rawMessage || 'n.a.';

    const serializedEvent = !rawMessage ? serializeEventProps(errorEvent) : null;

    return {
        message,
        filename: ('filename' in errorEvent && (errorEvent as ErrorEvent).filename) || 'n.a.',
        lineno:
            ('lineno' in errorEvent && (errorEvent as ErrorEvent).lineno?.toString()) ||
            ('lineNumber' in errorEvent &&
                (errorEvent as Error & { lineNumber?: number }).lineNumber?.toString()) ||
            'n.a.',
        colno:
            ('colno' in errorEvent && (errorEvent as ErrorEvent).colno?.toString()) ||
            ('columnNumber' in errorEvent &&
                (errorEvent as Error & { columnNumber?: number }).columnNumber?.toString()) ||
            'n.a.',
        stack:
            ('stack' in errorEvent && errorEvent.stack) ||
            ('error' in errorEvent && (errorEvent as ErrorEvent).error?.stack) ||
            'n.a.',
        eventType,
        serializedEvent
    };
};

const sendToBackend = async (errorEvent: OurError) => {
    // Log error otherwise it gets swallowed
    console.error(errorEvent);
    const errorLogFrontendUrl = window.logging.url;
    const errorLogToken = window.logging.token;

    if (!errorLogFrontendUrl || !errorLogToken) {
        return;
    }

    // Filter exceptions from Chrome extensions
    if (
        ('filename' in errorEvent &&
            (errorEvent as ErrorEvent).filename?.includes('chrome-extension:')) ||
        ('message' in errorEvent && errorEvent.message?.includes('chrome-extension:')) ||
        ('stack' in errorEvent && errorEvent.stack?.includes('chrome-extension:')) ||
        ('error' in errorEvent &&
            (errorEvent as ErrorEvent).error?.stack?.includes('chrome-extension:'))
    ) {
        return;
    }

    // Filter error if Google Tag Manager is blocked by user
    if ('target' in errorEvent) {
        const target = errorEvent.target as HTMLScriptElement;
        if (target?.src?.includes('googletagmanager')) {
            return;
        }
    }

    const errorDetails = buildErrorDetails(errorEvent);

    const errorData = {
        token: errorLogToken,
        level: 'error',
        content: {
            error: {
                ...errorDetails,
                timestamp: new Date()
            },
            userData: {
                agent: navigator.userAgent,
                language: navigator.language,
                locale: document.querySelector('html')?.getAttribute('lang'),
                url: document.URL
            }
        }
    };

    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const request = {
        method: 'POST',
        headers,
        body: safeStringify(errorData)
    };

    try {
        await fetch(errorLogFrontendUrl, request);
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message);
        }
    }
};

window.addEventListener('error', sendToBackend, true);
window.addEventListener('unhandledrejection', sendToBackend, true);
